import { Label, Radio, RadioGroup } from "@headlessui/react";
import { UserPlusIcon } from "@heroicons/react/24/solid";
import { AdminImportStudent, AdminImportStudentIdentification } from "../rosterImportApiSlice";
import { formatMatchIdentification, formatStudentInfo } from "./conflictReviewUtils";

interface ResolveStudentStepProps {
	student?: AdminImportStudent;
	value: number | null;
	onChange: (matchId: number | null) => void;
}

const cardBase = "cursor-pointer rounded-lg border p-2 text-sm";
const uncheckedCardBase = "border-gray-200 bg-white hover:bg-gray-50";
const dotBase = "size-4 rounded-full border";

const matchCardClass = (checked: boolean) =>
	[
		cardBase,
		checked ? "border-primary-600 bg-primary-50 ring-1 ring-primary-600" : uncheckedCardBase,
	].join(" ");

const newCardClass = (checked: boolean) =>
	[
		cardBase,
		checked ? "border-green-600 bg-green-50 ring-1 ring-green-600" : uncheckedCardBase,
	].join(" ");

const dotClass = (checked: boolean, variant: "primary" | "green") =>
	[
		dotBase,
		checked
			? variant === "primary"
				? "border-primary-600 bg-primary-600"
				: "border-green-600 bg-green-600"
			: "border-gray-300 bg-white",
	].join(" ");

function MatchOption({
	match,
	checked,
}: {
	match: AdminImportStudentIdentification;
	checked: boolean;
}) {
	return (
		<div className="flex items-start justify-between gap-2">
			<div className="min-w-0">
				<div className="truncate font-medium text-gray-900">
					{match.lName}, {match.fName}
				</div>
				<div className="text-xs text-gray-600">
					{formatMatchIdentification(match, false)}
				</div>
			</div>
			<div className={dotClass(checked, "primary")} />
		</div>
	);
}

function CreateNewOption({ checked }: { checked: boolean }) {
	return (
		<div className="flex items-start justify-between gap-2">
			<div className="min-w-0">
				<div className="flex items-center gap-2 font-medium text-gray-900">
					<UserPlusIcon className="size-5 text-green-700" aria-hidden="true" />
					Create New User
				</div>
				<div className="text-xs text-gray-600">
					A new account will be created for this student.
				</div>
			</div>
			<div className={dotClass(checked, "green")} />
		</div>
	);
}

export function ResolveStudentStep({ student, value, onChange }: ResolveStudentStepProps) {
	if (!student) return null;

	const selectedOption: number | null = value ?? null;

	return (
		<div>
			<h2 className="text-sm">
				Row {student.row}: {student.lName}, {student.fName}
			</h2>
			<div className="text-xs text-gray-600">{formatStudentInfo(student, false)}</div>
			<div className="mt-4">
				<RadioGroup value={selectedOption} onChange={onChange}>
					<Label className="block mb-2 text-sm font-medium text-gray-90">
						Resolution
					</Label>
					<div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
						{student.identifications.map((match) => (
							<Radio
								key={match.id}
								value={match.id}
								className={({ checked }) => matchCardClass(checked)}>
								{({ checked }) => <MatchOption match={match} checked={checked} />}
							</Radio>
						))}
						<Radio value={null} className={({ checked }) => newCardClass(checked)}>
							{({ checked }) => <CreateNewOption checked={checked} />}
						</Radio>
					</div>
				</RadioGroup>
			</div>
		</div>
	);
}
